export interface TermsSection {
  title: string;
  titleFr: string;
  content: string;
  contentFr: string;
  details?: Array<{ en: string; fr: string }>;
}

export const termsContent: TermsSection[] = [
  {
    title: "1. Acceptance of Terms",
    titleFr: "1. Acceptation des Conditions",
    content: "By accessing or using Sayaara's services, you agree to be bound by these Terms and Conditions. If you do not agree to these terms, please do not use our platform.",
    contentFr: "En accédant ou en utilisant les services de Sayaara, vous acceptez d'être lié par ces Conditions Générales. Si vous n'acceptez pas ces conditions, veuillez ne pas utiliser notre plateforme."
  },
  {
    title: "2. Description of Services",
    titleFr: "2. Description des Services",
    content: "Sayaara provides a platform that connects customers with dealerships offering Shariah-compliant car financing. We facilitate Murabaha and Musharaka financing structures, ensuring complete compliance with Islamic law.",
    contentFr: "Sayaara fournit une plateforme qui connecte les clients avec des concessionnaires offrant un financement automobile conforme à la Charia. Nous facilitons les structures de financement Murabaha et Musharaka, garantissant une conformité complète à la loi islamique.",
    details: [
      { en: "Direct dealership financing with transparent pricing", fr: "Financement direct par concessionnaire avec prix transparents" },
      { en: "Shariah-compliant financing structures (Murabaha/Musharaka)", fr: "Structures de financement conformes à la Charia (Murabaha/Musharaka)" },
      { en: "Contract drafting and compliance oversight", fr: "Rédaction de contrats et supervision de la conformité" },
      { en: "Dealership vetting and quality assurance", fr: "Vérification des concessionnaires et assurance qualité" }
    ]
  },
  {
    title: "3. User Obligations",
    titleFr: "3. Obligations de l'Utilisateur",
    content: "As a user of our platform, you agree to:",
    contentFr: "En tant qu'utilisateur de notre plateforme, vous acceptez de :",
    details: [
      { en: "Provide accurate, truthful, and complete personal and financial information", fr: "Fournir des informations personnelles et financières exactes, véridiques et complètes" },
      { en: "Immediately inform Sayaara of any changes in financial circumstances or personal details", fr: "Informer immédiatement Sayaara de tout changement de circonstances financières ou de détails personnels" },
      { en: "Use the platform only for legitimate financing purposes", fr: "Utiliser la plateforme uniquement à des fins de financement légitimes" },
      { en: "Comply with all applicable laws and regulations", fr: "Se conformer à toutes les lois et réglementations applicables" }
    ]
  },
  {
    title: "4. Eligibility Requirements",
    titleFr: "4. Conditions d'Éligibilité",
    content: "To use our services, you must meet the following criteria:",
    contentFr: "Pour utiliser nos services, vous devez répondre aux critères suivants :",
    details: [
      { en: "Be at least 18 years of age", fr: "Avoir au moins 18 ans" },
      { en: "Have legal residency in the United States or Canada", fr: "Avoir une résidence légale aux États-Unis ou au Canada" },
      { en: "Meet the minimum credit score requirements for your selected vehicle", fr: "Répondre aux exigences de score de crédit minimum pour votre véhicule sélectionné" },
      { en: "Provide valid identification and required documentation", fr: "Fournir une identification valide et la documentation requise" }
    ]
  },
  {
    title: "5. Financing & Purchase Process",
    titleFr: "5. Processus de Financement et d'Achat",
    content: "Our financing process is designed to be transparent and Shariah-compliant:",
    contentFr: "Notre processus de financement est conçu pour être transparent et conforme à la Charia :",
    details: [
      { en: "All financing uses Murabaha (cost-plus sale) or Musharaka (partnership) structures", fr: "Tout financement utilise des structures Murabaha (vente à coût majoré) ou Musharaka (partenariat)" },
      { en: "Dealerships provide financing directly - no banks or third parties involved", fr: "Les concessionnaires fournissent le financement directement - aucune banque ou tiers impliqué" },
      { en: "Fixed terms and pricing for all qualified buyers", fr: "Conditions et prix fixes pour tous les acheteurs qualifiés" },
      { en: "Contracts are reviewed by Islamic finance scholars for compliance", fr: "Les contrats sont examinés par des spécialistes de la finance islamique pour la conformité" }
    ]
  },
  {
    title: "6. Credit Requirements & Approval",
    titleFr: "6. Exigences de Crédit et Approbation",
    content: "Our approval process is straightforward:",
    contentFr: "Notre processus d'approbation est simple :",
    details: [
      { en: "Each vehicle listing shows the minimum credit score required", fr: "Chaque annonce de véhicule indique le score de crédit minimum requis" },
      { en: "Approval is based on meeting minimum requirements - pass or fail", fr: "L'approbation est basée sur le respect des exigences minimales - réussite ou échec" },
      { en: "All qualified buyers receive the same advertised terms", fr: "Tous les acheteurs qualifiés reçoivent les mêmes conditions annoncées" },
      { en: "No variable pricing based on credit scores", fr: "Aucun prix variable basé sur les scores de crédit" }
    ]
  },
  {
    title: "7. Vehicle Listings & Dealerships",
    titleFr: "7. Annonces de Véhicules et Concessionnaires",
    content: "We maintain high standards for our partner dealerships:",
    contentFr: "Nous maintenons des normes élevées pour nos concessionnaires partenaires :",
    details: [
      { en: "All dealerships undergo rigorous vetting for ethical standards", fr: "Tous les concessionnaires subissent une vérification rigoureuse des normes éthiques" },
      { en: "Vehicle information is provided by dealerships and verified when possible", fr: "Les informations sur les véhicules sont fournies par les concessionnaires et vérifiées quand possible" },
      { en: "Dealerships are responsible for vehicle condition and accuracy of listings", fr: "Les concessionnaires sont responsables de l'état du véhicule et de l'exactitude des annonces" },
      { en: "We facilitate the connection but dealerships handle the final transaction", fr: "Nous facilitons la connexion mais les concessionnaires gèrent la transaction finale" }
    ]
  },
  {
    title: "8. Shariah Compliance",
    titleFr: "8. Conformité à la Charia",
    content: "We are committed to Islamic finance principles:",
    contentFr: "Nous nous engageons aux principes de la finance islamique :",
    details: [
      { en: "All financing products are completely free from interest (Riba)", fr: "Tous les produits de financement sont complètement exempts d'intérêt (Riba)" },
      { en: "Contracts are structured to avoid uncertain transactions (Gharar)", fr: "Les contrats sont structurés pour éviter les transactions incertaines (Gharar)" },
      { en: "Financing is backed by real assets, not speculation", fr: "Le financement est soutenu par de vrais actifs, pas par la spéculation" },
      { en: "Regular review by qualified Islamic finance scholars", fr: "Révision régulière par des spécialistes qualifiés en finance islamique" }
    ]
  },
  {
    title: "9. Payments & Fees",
    titleFr: "9. Paiements et Frais",
    content: "Our pricing is transparent with no hidden costs:",
    contentFr: "Notre tarification est transparente sans coûts cachés :",
    details: [
      { en: "Fixed monthly payments as advertised", fr: "Paiements mensuels fixes comme annoncés" },
      { en: "No hidden fees or surprise charges", fr: "Aucun frais caché ou charge surprise" },
      { en: "All costs disclosed upfront in your purchase agreement", fr: "Tous les coûts divulgués à l'avance dans votre accord d'achat" },
      { en: "Payment responsibilities clearly outlined in your contract", fr: "Responsabilités de paiement clairement énoncées dans votre contrat" }
    ]
  },
  {
    title: "10. Cancellation & Returns",
    titleFr: "10. Annulation et Retours",
    content: "Understanding your options:",
    contentFr: "Comprendre vos options :",
    details: [
      { en: "Cancellation available before signing final purchase documents", fr: "Annulation disponible avant la signature des documents d'achat finaux" },
      { en: "Return policies vary by dealership and will be clearly explained", fr: "Les politiques de retour varient selon le concessionnaire et seront clairement expliquées" },
      { en: "Refund procedures depend on the stage of your purchase process", fr: "Les procédures de remboursement dépendent de l'étape de votre processus d'achat" },
      { en: "Contact us immediately if you need to cancel or return", fr: "Contactez-nous immédiatement si vous devez annuler ou retourner" }
    ]
  },
  {
    title: "11. Liability & Limitations",
    titleFr: "11. Responsabilité et Limitations",
    content: "Important limitations on our liability:",
    contentFr: "Limitations importantes sur notre responsabilité :",
    details: [
      { en: "Sayaara facilitates connections but is not responsible for vehicle condition", fr: "Sayaara facilite les connexions mais n'est pas responsable de l'état du véhicule" },
      { en: "Dealership disputes should be resolved directly with the dealership", fr: "Les disputes avec le concessionnaire doivent être résolues directement avec le concessionnaire" },
      { en: "We provide contract oversight but final terms are between you and the dealership", fr: "Nous fournissons la supervision du contrat mais les termes finaux sont entre vous et le concessionnaire" },
      { en: "Limitation of liability for indirect or consequential damages", fr: "Limitation de responsabilité pour les dommages indirects ou consécutifs" }
    ]
  },
  {
    title: "12. Intellectual Property",
    titleFr: "12. Propriété Intellectuelle",
    content: "All content on the Sayaara platform, including logos, designs, text, and software, is the intellectual property of Sayaara and is protected by applicable copyright and trademark laws.",
    contentFr: "Tout le contenu de la plateforme Sayaara, y compris les logos, designs, textes et logiciels, est la propriété intellectuelle de Sayaara et est protégé par les lois applicables sur les droits d'auteur et les marques de commerce."
  },
  {
    title: "13. Governing Law",
    titleFr: "13. Loi Applicable",
    content: "These Terms and Conditions are governed by the laws of Canada and the United States, depending on your location. Any disputes will be resolved through binding arbitration in accordance with applicable laws.",
    contentFr: "Ces Conditions Générales sont régies par les lois du Canada et des États-Unis, selon votre localisation. Tout litige sera résolu par arbitrage obligatoire conformément aux lois applicables."
  },
  {
    title: "14. Changes to Terms",
    titleFr: "14. Modifications des Conditions",
    content: "Sayaara reserves the right to modify these terms at any time. Changes will be posted on our website and users will be notified. Your continued use of the platform constitutes acceptance of any changes.",
    contentFr: "Sayaara se réserve le droit de modifier ces conditions à tout moment. Les modifications seront publiées sur notre site web et les utilisateurs seront notifiés. Votre utilisation continue de la plateforme constitue l'acceptation de tout changement."
  },
  {
    title: "15. Contact Information",
    titleFr: "15. Informations de Contact",
    content: "For questions about these Terms and Conditions, please contact us:",
    contentFr: "Pour des questions sur ces Conditions Générales, veuillez nous contacter :"
  }
];